import { useRef } from 'react';
import { formatBytes, isAllowedLogName, looksLikeText, SUPPORT_LOG_MAX_BYTES, SUPPORT_LOG_MAX_FILES, type SupportLogUpload } from '@va/shared';
import { useToast } from '../state/toast';
import { IconClose } from './icons';

export interface PickedLog extends SupportLogUpload {
  size: number;
}

/** Reads a log as text. Windows tools sometimes write UTF-16, so honour a byte-order mark. */
async function readLog(file: File): Promise<string> {
  const bytes = new Uint8Array(await file.arrayBuffer());
  if (bytes[0] === 0xff && bytes[1] === 0xfe) return new TextDecoder('utf-16le').decode(bytes.subarray(2));
  if (bytes[0] === 0xfe && bytes[1] === 0xff) return new TextDecoder('utf-16be').decode(bytes.subarray(2));
  const bom = bytes[0] === 0xef && bytes[1] === 0xbb && bytes[2] === 0xbf ? 3 : 0;
  return new TextDecoder('utf-8').decode(bytes.subarray(bom));
}

/**
 * "Attach log file" for support messages: .txt and .log only, up to 3 files of 4 MB, and the
 * contents must be text. The server checks all of this again; this just explains it early.
 */
export function LogUploader({ value, onChange }: { value: PickedLog[]; onChange: (logs: PickedLog[]) => void }) {
  const toast = useToast();
  const input = useRef<HTMLInputElement>(null);
  const maxMb = SUPPORT_LOG_MAX_BYTES / 1024 / 1024;

  const add = async (files: FileList | null) => {
    const next = [...value];
    for (const file of Array.from(files ?? [])) {
      if (next.length >= SUPPORT_LOG_MAX_FILES) {
        toast.error(`You can attach up to ${SUPPORT_LOG_MAX_FILES} log files.`);
        break;
      }
      if (!isAllowedLogName(file.name)) {
        toast.error(`"${file.name}" isn't a log file. Only .txt and .log files can be attached.`);
        continue;
      }
      if (file.size > SUPPORT_LOG_MAX_BYTES) {
        toast.error(`"${file.name}" is over ${maxMb} MB. Send the newest part of the log instead.`);
        continue;
      }
      const content = await readLog(file);
      if (!content.trim()) {
        toast.error(`"${file.name}" is empty.`);
        continue;
      }
      if (!looksLikeText(content)) {
        toast.error(`"${file.name}" doesn't look like a text log, so it can't be attached.`);
        continue;
      }
      next.push({ name: file.name, content, size: file.size });
    }
    onChange(next);
    if (input.current) input.current.value = '';
  };

  return (
    <div className="field">
      <label>Log files (optional)</label>
      {value.length > 0 && (
        <ul className="log-list">
          {value.map((log, i) => (
            <li key={`${log.name}-${i}`} className="log-chip">
              <span className="mono">{log.name}</span>
              <span className="dim">{formatBytes(log.size)}</span>
              <button type="button" className="icon-btn" aria-label={`Remove ${log.name}`} onClick={() => onChange(value.filter((_, j) => j !== i))}>
                <IconClose size={14} />
              </button>
            </li>
          ))}
        </ul>
      )}
      <div>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => input.current?.click()} disabled={value.length >= SUPPORT_LOG_MAX_FILES}>
          Attach log file
        </button>
        <input ref={input} type="file" accept=".txt,.log,text/plain" multiple hidden onChange={(e) => void add(e.target.files)} />
      </div>
      <span className="hint">
        .txt or .log only, up to {SUPPORT_LOG_MAX_FILES} files of {maxMb} MB. Resolume's log on Windows:{' '}
        <span className="mono">%APPDATA%\Resolume Arena\Resolume Arena log.txt</span> (paste that into File Explorer's address bar). On a Mac:{' '}
        <span className="mono">~/Library/Application Support/Resolume Arena</span> (Finder › Go › Go to Folder).
      </span>
    </div>
  );
}

/** Log files on a ticket message, for customers: names and sizes. */
export function LogList({ files }: { files: { id: string; fileName: string; sizeBytes: number }[] }) {
  if (!files.length) return null;
  return (
    <ul className="log-list" style={{ marginTop: 10 }}>
      {files.map((f) => (
        <li key={f.id} className="log-chip">
          <span className="mono">{f.fileName}</span>
          <span className="dim">{formatBytes(f.sizeBytes)}</span>
        </li>
      ))}
    </ul>
  );
}
